'use client';

import type * as Iso3166Namespace from 'iso-3166-2';
import { useEffect, useState } from 'react';

export type Iso3166Module = typeof Iso3166Namespace;

let cached: Iso3166Module | null = null;
let loadPromise: Promise<Iso3166Module> | null = null;

/**
 * Lazy-load the `iso-3166-2` package. Wrapping the `import()` in a function
 * keeps the package's ~239 KB parsed bytes out of `_app.js`'s static-import
 * graph (ARC-194) — webpack puts it in a dedicated async chunk that only
 * resolves when a component that needs subdivision data actually mounts.
 *
 * The first call returns the in-flight Promise; subsequent calls reuse the
 * cached module.
 */
export const loadIso3166 = (): Promise<Iso3166Module> => {
  if (cached) return Promise.resolve(cached);
  loadPromise ??= import('iso-3166-2').then((m) => {
    cached = m;
    return m;
  });
  return loadPromise;
};

/**
 * Returns the cached `iso-3166-2` module or `null` if it hasn't loaded yet.
 * Use inside synchronous code paths (e.g. inside `useMemo` callbacks) and
 * gate behaviour on the result. Pair with `useIso3166()` so the component
 * re-renders once the module resolves.
 */
export const getIso3166Sync = (): Iso3166Module | null => cached;

/**
 * React hook that triggers a lazy load of `iso-3166-2` on mount and returns
 * the module once available (`null` until then). Components that compute
 * derived values from the module should include the result in their
 * dependency arrays so memoised computations re-run when the module
 * finishes loading.
 */
export function useIso3166(): Iso3166Module | null {
  const [iso, setIso] = useState<Iso3166Module | null>(cached);

  useEffect(() => {
    if (iso) return;

    let cancelled = false;

    void loadIso3166().then((m) => {
      if (!cancelled) setIso(m);
    });

    return () => {
      cancelled = true;
    };
  }, [iso]);
  return iso;
}
